import { Output, EventEmitter } from '@angular/core';
import { Injectable } from '@angular/core';
import { Router, ActivatedRoute, ParamMap, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClient, HttpHeaders, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, retry, tap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

import { environment } from './../environments/environment';
import { IRoughcheck, IAssessment, ISection, IQuestion, IRisk, IReward, IQFeedback } from './roughcheck';
import { MessageService } from './message.service';
import { LoggerService } from './logger.service';

/**
 * Service to fetch a Roughcheck
 * and maintain state
 */
@Injectable({
  providedIn: 'root'
})
export class RoughcheckService {
  // BehaviorSubject enabling the 'current value' to be always available
  roughcheckRRef = new BehaviorSubject<string>('');
  assessmentARef = new BehaviorSubject<string>('');

  private roughcheckUrl = environment.apiUrl + 'api/v1/assessment';
  private pageUrl = environment.apiUrl + 'api/v1/pages';
  private ERROR_RPIN_REQUIRED = environment.ERROR_RPIN_REQUIRED;
  private ERROR_APIN_REQUIRED = environment.ERROR_APIN_REQUIRED;
  private ERROR_INACTIVE = environment.ERROR_INACTIVE;
  private gotopage = '';
  private pin = false;
  private roughcheck: IRoughcheck = {
    rref: '',
    title: '',
    image: '',
    image_alt: '',
    category: '',
    riskprofile: '',
    rewardprofile: '',
    show_risk_reward: 1,
    description: '',
    slug: '',
    status: 0,
    status_email: 0,
    feedback: 0,
    sponsor: '',
    website: ''
  };
  private assessment: IAssessment = {aref: ''};
  private sections: ISection[];
  private section: ISection;
  private risks: IRisk[];
  private rewards: IReward[];
  private question: IQuestion;
  private page: string;

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  constructor(
    private router: Router,
    private http: HttpClient,
    private message: MessageService,
    private logger: LoggerService,
  ) { }

  /**
   * Return a Roughcheck from a given reference
   * @param result - Roughcheck object
   */
  getHttpRoughcheck(): Observable<HttpResponse<any>> {
    if (this.assessment.aref && this.roughcheck.rref) {
      const url = this.roughcheckUrl + '/' + this.roughcheck.rref + '/' + this.assessment.aref + '.json';
      return this.http.get(url, { observe: 'response' });
    } else if (this.roughcheck.rref) {
      const url = this.roughcheckUrl + '/' + this.roughcheck.rref + '.json';
      return this.http.get(url, { observe: 'response' });
    } else {
      // Ignore as there is nothing to get
    }
  }

  /**
   * Update a Roughcheck assessment
   * @param result - Assessment object
   */
  setAssessment(data: IAssessment): void {
    this.assessment = data;
    this.assessmentARef.next(this.assessment.aref);
  }

  /**
   * Create or update a Roughcheck assessment
   * @param result - Assessment object
   */
  setHttpAssessment(data: IAssessment): Observable<HttpResponse<IAssessment>> {
    this.assessment = data;
    if (this.roughcheck.rref && this.assessment.aref) {
      const url = this.roughcheckUrl + '/' + this.roughcheck.rref + '/' + this.assessment.aref + '.json';
      return this.http.post(url, this.assessment, { observe: 'response' });
    } else if (this.roughcheck.rref) {
      const url = this.roughcheckUrl + '/' + this.roughcheck.rref + '.json';
      return this.http.post(url, this.assessment, { observe: 'response' });
    } else {
      const msg = 'RoughcheckService RRef must be set';
      const e = new HttpErrorResponse({ error: msg, status: 500, statusText: 'Internal error'});
      this.logger.logError(this.roughcheck.rref, this.assessment.aref, e, msg);
      throw e;
    }
  }

  /**
   * Get a Roughcheck assessment assessment
   * @result - Assessment object
   */
  getAssessment(): IAssessment {
    return this.assessment;
  }

  /**
   * Return help for a question
   * @param result - help array
   */
  getHttpPage(): Observable<HttpResponse<any>> {
    if ( ! this.page ) { return; }
    const url = this.pageUrl + '/' + this.page + '.json';
    return this.http.get(url, { observe: 'response' });
  }

  /**
   * Create or Update a Roughcheck assessment question
   * After an update, replace objects as necessary to keep questions and
   * current question in sync to avoid object identity mismatch issues
   * @param result - Question object
   */
  setHttpQuestion(data: IQuestion): Observable<HttpResponse<any>> {
    if (this.assessment.aref && this.roughcheck.rref) {
      const url = this.roughcheckUrl
        + '/' + this.roughcheck.rref
        + '/' + this.assessment.aref
        + '/section'
        + '/' + data.section
        + '/' + data.id + '.json';
      return this.http.put(url, data, { observe: 'response' });
    } else {
      const msg = 'RoughcheckService RRef and ARef must be set to answer questions';
      const e = new HttpErrorResponse({ error: msg, status: 500, statusText: 'Internal error'});
      this.logger.logError(this.roughcheck.rref, this.assessment.aref, e, msg);
      throw e;
    }
  }

  /**
   * Create or Update a Roughcheck question feedback
   * @param result - QFeedbackobject
   */
  setHttpQFeedback(data: IQuestion, feedback: IQFeedback): Observable<HttpResponse<any>> {
    // console.debug(data);
    // console.debug(feedback);
    if (this.assessment.aref && this.roughcheck.rref) {
      const url = this.roughcheckUrl
        + '/' + this.roughcheck.rref
        + '/' + this.assessment.aref
        + '/section'
        + '/' + data.section
        + '/' + data.id + '.json';
      return this.http.put(url, feedback, { observe: 'response' });
    } else {
      const msg = 'RoughcheckService RRef and ARef must be set to answer questions';
      const e = new HttpErrorResponse({ error: msg, status: 500, statusText: 'Internal error'});
      this.logger.logError(this.roughcheck.rref, this.assessment.aref, e, msg);
      throw e;
    }
  }

  /**
   * Return help for a question
   * @param result - help array
   */
  getHttpQuestionHelp(section, question): Observable<HttpResponse<any>> {
    if (this.assessment.aref && this.roughcheck.rref) {
      const url = this.roughcheckUrl
        + '/' + this.roughcheck.rref + '/' + this.assessment.aref
        + '/section/' + section
        + '/' + question
        + '/help.json';
      return this.http.get(url, { observe: 'response' });
    } else if (this.roughcheck.rref) {
      const url = this.roughcheckUrl
        + '/' + this.roughcheck.rref
        + '/section' + section
        + '/' + question
        + '/help.json';
      return this.http.get(url, { observe: 'response' });
    }
  }

  /**
   * Return feedback for a question
   * @param result - feedback
   */
  getHttpQFeedback(question): Observable<HttpResponse<any>> {
    if (this.assessment.aref && this.roughcheck.rref) {
      const url = this.roughcheckUrl
        + '/' + this.roughcheck.rref + '/' + this.assessment.aref
        + '/section/' + this.section.id
        + '/' + question.id
        + '/feedback.json';
      return this.http.get(url, { observe: 'response' });
    } else if (this.roughcheck.rref) {
      const url = this.roughcheckUrl
        + '/' + this.roughcheck.rref
        + '/section/' + this.section.id
        + '/' + question
        + '/feedback.json';
      return this.http.get(url, { observe: 'response' });
    }
  }

  /**
   * List of sections for a roughcheck
   * @result ISection[]
   */
  getHttpSections(): Observable<any> {
    if (this.roughcheck.rref && this.assessment.aref) {
      const url = this.roughcheckUrl + '/' + this.roughcheck.rref + '/' + this.assessment.aref + '/section.json';
      return this.http.get(url, { observe: 'response' });
    } else if (this.roughcheck.rref) {
      const url = this.roughcheckUrl + '/' + this.roughcheck.rref + '/section.json';
      return this.http.get(url, { observe: 'response' });
    } else {
      // Ignore as there is nothing to get
    }
  }
  /**
   * List of questions for a roughcheck section
   * @result IQuestion[]
   */
  getHttpQuestions(id: string): Observable<any> {
    if (this.assessment.aref && this.roughcheck.rref) {
      const url = this.roughcheckUrl + '/' + this.roughcheck.rref + '/' + this.assessment.aref + '/section/' + id + '.json';
      return this.http.get(url, { observe: 'response' });
    } else if (this.roughcheck.rref) {
      const url = this.roughcheckUrl + '/' + this.roughcheck.rref + '/section/' + id + '.json';
      return this.http.get(url, { observe: 'response' });
    } else {
      const msg = 'RoughcheckService RRef must be set to get questions';
      const e = new HttpErrorResponse({ error: msg, status: 500, statusText: 'Internal error'});
      this.logger.logError(this.roughcheck.rref, this.assessment.aref, e, msg);
      throw e;
    }
  }

  /**
   * List of risks for a roughcheck
   * @result Risks[]
   */
  getHttpRisks(): Observable<any> {
    if (this.roughcheck.rref) {
      const url = environment.apiUrl + 'api/v1/riskprofile/' + this.roughcheck.rref + '.json';
      return this.http.get(url, { observe: 'response' });
    } else {
      const msg = 'RoughcheckService RRef must be set to get risks';
      const e = new HttpErrorResponse({ error: msg, status: 500, statusText: 'Internal error'});
      this.logger.logError(this.roughcheck.rref, this.assessment.aref, e, msg);
      throw e;
    }
  }

  /**
   * List of rewards for a roughcheck
   * @result Rewards[]
   */
  getHttpRewards(): Observable<any> {
    if (this.roughcheck.rref) {
      const url = environment.apiUrl + 'api/v1/rewardprofile/' + this.roughcheck.rref + '.json';
      return this.http.get(url, { observe: 'response' });
    } else {
      const msg = 'RoughcheckService RRef must be set to get rewards';
      const e = new HttpErrorResponse({ error: msg, status: 500, statusText: 'Internal error'});
      this.logger.logError(this.roughcheck.rref, this.assessment.aref, e, msg);
      throw e;
    }
  }

  /**
   * Roughcheck assessment report
   * @result IReport[]
   */
  getHttpReport(): Observable<any> {
    if (this.assessment.aref) {
      const url = this.roughcheckUrl + '/' + this.roughcheck.rref + '/' + this.assessment.aref + '/report.json';
      return this.http.get(url, { observe: 'response' });
    } else {
      const msg = 'RoughcheckService ARef must be set to get a report';
      const e = new HttpErrorResponse({ error: msg, status: 500, statusText: 'Internal error'});
      this.logger.logError(this.roughcheck.rref, this.assessment.aref, e, msg);
      throw e;
    }
  }

  /**
   * Roughcheck assessment actions
   * @result IAction[]
   */
  getHttpActions(): Observable<any> {
    if (this.assessment.aref) {
      const url = this.roughcheckUrl + '/' + this.roughcheck.rref + '/' + this.assessment.aref + '/actions.json';
      return this.http.get(url, { observe: 'response' });
    } else {
      const msg = 'RoughcheckService ARef must be set to get actions';
      const e = new HttpErrorResponse({ error: msg, status: 500, statusText: 'Internal error'});
      this.logger.logError(this.roughcheck.rref, this.assessment.aref, e, msg);
      throw e;
    }
  }

  /**
   * Handle error responses
   */
  handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An ErrorEvent occurred:', error.error.message);
      // this.service.setError('500');
      this.logger.logError(
        this.roughcheck.rref,
        this.assessment.aref,
        error,
        error.error.message
      ).subscribe();
    } else if (error.error instanceof HttpErrorResponse) {
      // A client-side or network error occurred. Handle it accordingly.
      // this.service.setError('500');
      console.error('An HttpErrorResponse error occurred:', error.error.message);
      this.logger.logError(
        this.roughcheck.rref,
        this.assessment.aref,
        error,
        error.error.message
      ).subscribe();
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      this.logger.logError(
        this.roughcheck.rref,
        this.assessment.aref,
        error,
        'Unknown response',
        ).subscribe();
      if (error.status === 404) {
        // this.service.setError('404');
        console.error(error);
        console.error('Roughcheck not found');
        const msg = 'Roughcheck not found';
        this.message.add(msg, 'info');
        this.router.navigate(['/home']);
      } else if (error.status === 403) {
        // this.service.setError('403');
        console.error(error);
        console.error('Roughcheck is protected with a pin');
        if (error.error[environment.ERROR_INACTIVE]) {
          console.error('This Roughcheck is not available');
          const msg = 'This Roughcheck is not available';
          this.message.add(msg, 'info');
          this.router.navigate(['/start']);
        } else if (error.error[environment.ERROR_APIN_REQUIRED]) {
          console.error('This Roughcheck Assessment is protected with a pin');
          this.pin = true;
          // const msg = 'Please enter the pin number';
          // this.message.add(msg, 'warning');
          this.router.navigate(['/start']);
        } else if (error.error[environment.ERROR_RPIN_REQUIRED]) {
          console.error('This Roughcheck is protected with a pin');
          this.pin = true;
          // const msg = 'Please enter the pin number';
          // this.message.add(msg, 'warning');
          this.router.navigate(['/start']);
        } else {
          this.router.navigate(['/home']);
        }
      } else {
        // this.service.setError('500');
        console.error(`Error object is type: ${typeof(error)}`);
        console.error(`Error status: ${error.status}`);
        console.error(error);
        console.error(
          `Backend returned code ${error.status}, ` +
          `body was: ${error.error}`);
      }
    }
    // return an observable with a user-facing error message
    return throwError('Something bad happened; please try again later.');
  }


  setRefFromUrl(snapshot: ActivatedRouteSnapshot): void {
    // Roughcheck
    if (snapshot.paramMap.get('rref')) {
      if (this.roughcheck.rref !== snapshot.paramMap.get('rref')) {
        this.roughcheck.rref = snapshot.paramMap.get('rref');
      }
    } else {
      this.roughcheck.rref = undefined;
    }
    // Assessment
    if (snapshot.paramMap.get('aref')) {
      if (this.assessment.aref !== snapshot.paramMap.get('aref')) {
        this.assessment.aref = snapshot.paramMap.get('aref');
      }
    } else {
      this.assessment.aref = undefined;
    }
  }

  /**
   * Get roughcheck.rref and ARef from url if available
   */
  gotoFromUrl(gotopage: string, snapshot: ActivatedRouteSnapshot): void {
    this.setRefFromUrl(snapshot);

    // Go to roughcheck assessment page to init this app
    if (this.assessment.aref) {
      this.gotopage = gotopage;
      this.router.navigate(['/a', this.roughcheck.rref, this.assessment.aref]);
    } else if (this.roughcheck.rref) {
      this.gotopage = gotopage;
      this.router.navigate(['/a', this.roughcheck.rref]);
    } else {
      this.router.navigate(['/start']);
    }
  }

  // Getters
  getRRef(): string {
    if (this.roughcheck.rref) {
      return this.roughcheck.rref;
    } else {
      return '';
    }
  }
  getARef(): string {
    if (this.assessment.aref) {
      return this.assessment.aref;
    } else {
      return '';
    }
  }
  getPage(): string {
    return this.page;
  }
  getGoToPage(): string {
   return this.gotopage;
  }
  getPin(): boolean {
    return this.pin;
  }
  getRoughcheck(): IRoughcheck {
    return this.roughcheck;
  }
  getSection(): ISection {
    return this.section;
  }
  getRisks(): IRisk[] {
    return this.risks;
  }
  getRewards(): IReward[] {
    return this.rewards;
  }
  getQuestion(): IQuestion {
    return this.question;
  }
  getSections(): ISection[] {
    return this.sections;
  }

  setRoughcheck(data: IRoughcheck) {
    this.roughcheck = data;
    this.roughcheckRRef.next(this.roughcheck.rref);
  }

  // Setters
  setPin(pin: boolean) {
    this.pin = pin;
  }
  setSections(data: ISection[]) {
    this.sections = data;
  }
  setSection(data: ISection) {
    this.section = data;
  }
  setRisks(data: IRisk[]) {
    this.risks = data;
  }
  setRewards(data: IReward[]) {
    this.rewards = data;
  }
  setPage(data: string) {
    this.page = data;
  }
  setGoToPage(data: string) {
   this.gotopage = data;
  }

  setQuestion(data: IQuestion): void {
    this.question = data;
  }

}
